"use client";

import { Link } from "@/i18n/navigation";
import { getTodaysDailyPuzzle } from "@/lib/daily-puzzle";
import {
    checkIfOpponentFirst,
    parseSolution,
} from "@/plugins/chessboard/utilities/helpers";
import { Chess } from "chess.js";
import Image from "next/image";
import { useEffect, useState } from "react";

import { Spinner } from "@/components/shadcn/spinner";
import "@/plugins/chessboard/assets/scss/styles.scss";
import PuzzleBoardCard from "./puzzle-board-card";

interface Puzzle {
    Id: number;
    Fen: string;
    Rating: number;
    Answer: string;
    LastMove?: string | null;
}

export default function DailyPuzzleDisplay() {
    const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
    const [displayFen, setDisplayFen] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        async function fetchDailyPuzzle() {
            try {
                const puzzleData = await getTodaysDailyPuzzle();
                setPuzzle(puzzleData);

                if (puzzleData?.Fen && puzzleData?.Answer) {
                    setDisplayFen(
                        getStartingPosition(puzzleData.Fen, puzzleData.Answer),
                    );
                }
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An error occurred",
                );
            } finally {
                setLoading(false);
            }
        }

        fetchDailyPuzzle();
    }, []);

    if (loading) {
        return (
            <div className="bg-muted mx-auto mb-4 aspect-square h-auto w-full max-w-[500px] animate-pulse rounded-lg">
                <div className="text-muted-foreground flex h-full items-center justify-center">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (error || !puzzle) {
        // Fallback to original image if there's an error
        return (
            <Link href="/bulmacalar">
                <Image
                    src="/images/chessboard-start.jpg"
                    alt="Bulmacalar"
                    width={500}
                    height={500}
                    className="mx-auto mb-4 h-auto max-w-full"
                />
            </Link>
        );
    }

    return (
        <Link href="/bulmacalar/gunluk-bulmaca?scored=true">
            <PuzzleBoardCard
                answer={puzzle.Answer}
                fen={displayFen || puzzle.Fen}
            />
        </Link>
    );
}

function getStartingPosition(fen: string, solution: string): string {
    if (!fen || !solution) return fen;

    const chess = new Chess(fen);
    const parsedSolution = parseSolution(solution);

    if (!parsedSolution) return fen;

    const playerIndex =
        parsedSolution[parsedSolution.length - 1].length === 1 ? 0 : 1;
    const opponentIndex = playerIndex === 0 ? 1 : 0;

    const isOpponentFirst = checkIfOpponentFirst(parsedSolution);

    if (isOpponentFirst) {
        const turnSolution = parsedSolution[0];
        if (turnSolution) {
            const opponentTurnSan = turnSolution[opponentIndex];
            if (opponentTurnSan) {
                chess.move(opponentTurnSan);
            }
        }
    }

    return chess.fen();
}
